import { TFunction } from "i18next";
import { RewardsProgramType } from "@shared/enums/RewardsProgramType";
import { RewardsType } from "@shared/enums/RewardsType";
import { formatRewardsUoM, RewardsUom } from "@shared/enums/RewardsUom";
import { formatNumber, formatPercent } from "@shared/utilities/FormatUtils";

export const getRewardsValueText = (
  rewardsType: RewardsType | null,
  rewardsValue: number,
  uom: RewardsUom,
  t: TFunction,
  i18n: { language: string },
) => {
  if (uom === RewardsUom.Dollars) {
    const formattedValue = formatNumber(rewardsValue, i18n, { maximumFractionDigits: 4 });
    return t($ => $.customerDashboard.tierBenefits.pointsPerDollar, { value: formattedValue });
  } else if (rewardsType === RewardsType.Dollars) {
    const formattedValue = formatNumber(rewardsValue, i18n, { maximumFractionDigits: 4 });
    return `${formattedValue}/${formatRewardsUoM(1, uom)}`;
  } else {
    return formatPercent(
      parseFloat((rewardsValue * 100).toFixed(3)),
      i18n,
      { maximumFractionDigits: 3 },
    );
  }
};

export const buildRequirementTextForTier = (
  t: TFunction,
  tier: { minimumDollars: number, minimumSegments: number, name: string },
  isFinalTier: boolean,
  includeNextTierText = true,
) => {
  const hasSpend = tier.minimumDollars > 0;
  const hasSegments = tier.minimumSegments > 0;

  if (!hasSpend && !hasSegments) {
    return '';
  }

  const params = {
    tierName: tier.name,
    amount: tier.minimumDollars,
    count: tier.minimumSegments,
  };

  if (includeNextTierText) {
    if (hasSpend && hasSegments) {
      return isFinalTier
        ? t($ => $.customers.rewardsProgram.toMaintainSpendAndHurdle, params)
        : t($ => $.customers.rewardsProgram.toReachSpendAndHurdle, params);
    }
    if (hasSpend) {
      return isFinalTier
        ? t($ => $.customers.rewardsProgram.toMaintainSpend, params)
        : t($ => $.customers.rewardsProgram.toReachSpend, params);
    }
    return isFinalTier
      ? t($ => $.customers.rewardsProgram.toMaintainHurdle, params)
      : t($ => $.customers.rewardsProgram.toReachHurdle, params);
  }

  if (hasSpend && hasSegments) {
    return t($ => $.customers.rewardsProgram.spendAndHurdle, params);
  }
  if (hasSpend) {
    return t($ => $.customers.rewardsProgram.spendAmount, params);
  }
  return t($ => $.customers.rewardsProgram.hurdleCategory, params);
};

// Retailer screens show agronomy periods, tiers and points only; energy has its own blocks.
export const agronomyProgramsOnly = <T extends { type?: RewardsProgramType | null }> (
  programs: T[] | null | undefined,
): T[] => {
  const isAgronomy = ({ type }: T) => (
    (type ?? RewardsProgramType.Agronomy) === RewardsProgramType.Agronomy
  );
  return (programs ?? []).filter(isAgronomy);
};
